import {
  userLookupCatalog,
  orderLookupCatalog,
  parseContext,
  type Context,
  type Values,
} from "@proof/core";
const own = (row: unknown, key: string): unknown =>
  row && typeof row === "object" && Object.hasOwn(row, key)
    ? (row as Record<string, unknown>)[key]
    : undefined;
const text = (v: unknown) =>
  typeof v === "string"
    ? v
    : typeof v === "number" && Number.isFinite(v)
      ? String(v)
      : "";
/** Explicit numbering follows SoftProofingHelper.UserProfileLookup; 25 is intentionally unmapped. */
export function legacyUserContext(
  row: unknown,
  options: {
    authenticated?: boolean;
    guest?: boolean;
    fullName?: string;
    photoAssetId?: string;
    logoAssetId?: string;
  } = {},
): Values {
  if (
    !row ||
    typeof row !== "object" ||
    options.authenticated === false ||
    options.guest
  )
    return {};
  const get = (key: string) => text(own(row, key)),
    state = own(row, "ESM_STATES"),
    stateCode = text(own(state, "STATE_CODE")),
    stateName = text(own(state, "STATE"));
  for (const asset of [options.photoAssetId, options.logoAssetId])
    if (asset !== undefined && !/^asset_[a-zA-Z0-9_-]+$/.test(asset))
      throw new Error("Legacy profile images require managed asset IDs");
  const region = [stateCode, get("ZIP")].filter(Boolean).join(" "),
    city = get("CITY"),
    values: Record<number, string> = {
      1:
        options.fullName ??
        [get("FIRST_NAME"), get("LAST_NAME")].filter(Boolean).join(" "),
      2: get("PHONE_NUMBER"),
      3: get("FAX"),
      4: city && region ? `${city}, ${region}` : city || region,
      5: get("TITLE"),
      6: get("ADDRESS1"),
      7: get("ADDRESS2"),
      8: city,
      9: stateCode,
      10: get("ZIP"),
      11: get("FIRST_NAME"),
      12: get("LAST_NAME"),
      13: get("ADDRESS3"),
      14: get("EMAIL"),
      15: get("DIVISION_NAME"),
      16: get("CUSTOM01"),
      17: get("CUSTOM02"),
      18: get("CUSTOM03"),
      19: get("WEBSITE"),
      20: get("TITLE2"),
      21: get("PHONE_NUMBER2"),
      22: get("PHONE_NUMBER3"),
      23: options.photoAssetId || "",
      24: options.logoAssetId || "",
      26: get("EXTERNAL_ID"),
      27: stateName,
    };
  const result = Object.fromEntries(
    userLookupCatalog.flatMap((item) => [
      [item.key, values[item.id]],
      [`lookup_${item.id}`, values[item.id]],
    ]),
  );
  return parseContext({ user: result }).user!;
}
export function legacyOrderContext(row?: unknown, placeholders = true): Values {
  return Object.fromEntries(
    orderLookupCatalog.map((item) => [
      item.key,
      text(own(row, item.key)) || (placeholders ? item.placeholder : ""),
    ]),
  );
}
export function legacyHostContext(input: {
  user?: unknown;
  order?: unknown;
  authenticated?: boolean;
  guest?: boolean;
  fullName?: string;
  photoAssetId?: string;
  logoAssetId?: string;
}): Context {
  return parseContext({
    user: legacyUserContext(input.user, input),
    order: legacyOrderContext(input.order),
  });
}
